/**
 * Image transformation utilities for the image editor
 */

import { ImageTransformSettings } from './useImageEditor'
import { loadImage, applyDithering, createBMP, DISPLAY_WIDTH, DISPLAY_HEIGHT } from '../image-processing/imageProcessor'

/**
 * Apply rotation to a canvas
 */
function rotateCanvas(
  sourceCanvas: HTMLCanvasElement,
  rotation: 0 | 90 | 180 | 270
): HTMLCanvasElement {
  if (rotation === 0) return sourceCanvas

  const destCanvas = document.createElement('canvas')
  const ctx = destCanvas.getContext('2d')!

  if (rotation === 90 || rotation === 270) {
    destCanvas.width = sourceCanvas.height
    destCanvas.height = sourceCanvas.width
  } else {
    destCanvas.width = sourceCanvas.width
    destCanvas.height = sourceCanvas.height
  }

  ctx.save()
  ctx.translate(destCanvas.width / 2, destCanvas.height / 2)
  ctx.rotate((rotation * Math.PI) / 180)
  ctx.drawImage(
    sourceCanvas,
    -sourceCanvas.width / 2,
    -sourceCanvas.height / 2
  )
  ctx.restore()

  return destCanvas
}

/**
 * Apply flip transformations to a canvas
 */
function flipCanvas(
  sourceCanvas: HTMLCanvasElement,
  flipHorizontal: boolean,
  flipVertical: boolean
): HTMLCanvasElement {
  if (!flipHorizontal && !flipVertical) return sourceCanvas

  const destCanvas = document.createElement('canvas')
  destCanvas.width = sourceCanvas.width
  destCanvas.height = sourceCanvas.height
  const ctx = destCanvas.getContext('2d')!

  ctx.save()
  ctx.translate(
    flipHorizontal ? destCanvas.width : 0,
    flipVertical ? destCanvas.height : 0
  )
  ctx.scale(
    flipHorizontal ? -1 : 1,
    flipVertical ? -1 : 1
  )
  ctx.drawImage(sourceCanvas, 0, 0)
  ctx.restore()

  return destCanvas
}

/**
 * Apply crop to a canvas
 */
function cropCanvas(
  sourceCanvas: HTMLCanvasElement,
  crop: ImageTransformSettings['crop']
): HTMLCanvasElement {
  if (!crop.enabled) return sourceCanvas

  const destCanvas = document.createElement('canvas')
  destCanvas.width = crop.width
  destCanvas.height = crop.height
  const ctx = destCanvas.getContext('2d')!

  ctx.drawImage(
    sourceCanvas,
    crop.x, crop.y, crop.width, crop.height,
    0, 0, crop.width, crop.height
  )

  return destCanvas
}

/**
 * Resize image to fit display dimensions with scale and pan
 */
function resizeToDisplay(
  sourceCanvas: HTMLCanvasElement,
  targetWidth: number,
  targetHeight: number,
  scale: number = 1,
  panX: number = 0,
  panY: number = 0
): HTMLCanvasElement {
  const destCanvas = document.createElement('canvas')
  destCanvas.width = targetWidth
  destCanvas.height = targetHeight
  const ctx = destCanvas.getContext('2d')!

  // Fill with white background
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, targetWidth, targetHeight)

  // Calculate base scale to fit image in display
  const baseScale = Math.min(targetWidth / sourceCanvas.width, targetHeight / sourceCanvas.height)

  // Apply user scale on top of base scale
  const finalScale = baseScale * scale

  // Calculate scaled dimensions
  const scaledWidth = sourceCanvas.width * finalScale
  const scaledHeight = sourceCanvas.height * finalScale

  // Center position (before pan)
  const centerX = (targetWidth - scaledWidth) / 2
  const centerY = (targetHeight - scaledHeight) / 2

  // Apply pan offset (scaled proportionally)
  const dx = centerX + panX * finalScale
  const dy = centerY + panY * finalScale

  ctx.drawImage(sourceCanvas, dx, dy, scaledWidth, scaledHeight)

  return destCanvas
}

/**
 * Apply all transformations and process image for e-ink display
 */
export async function processImageWithSettings(
  source: string | File | Blob,
  settings: ImageTransformSettings
): Promise<{ bmpData: Uint8Array; previewDataUrl: string }> {
  // Load the original image
  const img = await loadImage(source)

  // Create initial canvas from image
  let canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0)

  // Apply crop first (if enabled)
  canvas = cropCanvas(canvas, settings.crop)

  // Apply rotation
  canvas = rotateCanvas(canvas, settings.rotation)

  // Apply flips
  canvas = flipCanvas(canvas, settings.flipHorizontal, settings.flipVertical)

  // Resize to display dimensions with scale and pan
  canvas = resizeToDisplay(canvas, DISPLAY_WIDTH, DISPLAY_HEIGHT, settings.scale, settings.panX, settings.panY)

  // Get image data for processing
  const imageData = canvas.getContext('2d')!.getImageData(0, 0, DISPLAY_WIDTH, DISPLAY_HEIGHT)

  // Apply dithering with selected algorithm
  const processed = applyDithering(imageData, settings.ditherAlgorithm)

  // Create preview image from processed data
  const previewCanvas = document.createElement('canvas')
  previewCanvas.width = DISPLAY_WIDTH
  previewCanvas.height = DISPLAY_HEIGHT
  const previewCtx = previewCanvas.getContext('2d')!
  previewCtx.putImageData(processed, 0, 0)
  const previewDataUrl = previewCanvas.toDataURL('image/png')

  // Convert to BMP
  const bmpData = createBMP(processed)

  return { bmpData, previewDataUrl }
}

/**
 * Generate a quick preview of the image with transforms applied (without full dithering)
 * This is faster for real-time preview updates
 */
export async function generateQuickPreview(
  source: string | File | Blob,
  settings: ImageTransformSettings,
  previewSize: number = 200
): Promise<string> {
  // Load the original image
  const img = await loadImage(source)

  // Create initial canvas from image
  let canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0)

  // Apply crop first (if enabled)
  canvas = cropCanvas(canvas, settings.crop)

  // Apply rotation
  canvas = rotateCanvas(canvas, settings.rotation)

  // Apply flips
  canvas = flipCanvas(canvas, settings.flipHorizontal, settings.flipVertical)

  // Scale down for preview
  const aspectRatio = DISPLAY_WIDTH / DISPLAY_HEIGHT
  const previewHeight = previewSize
  const previewWidth = previewHeight * aspectRatio

  const previewCanvas = document.createElement('canvas')
  previewCanvas.width = previewWidth
  previewCanvas.height = previewHeight
  const previewCtx = previewCanvas.getContext('2d')!

  // Fill with white background
  previewCtx.fillStyle = 'white'
  previewCtx.fillRect(0, 0, previewWidth, previewHeight)

  // Calculate base scale
  const baseScale = Math.min(previewWidth / canvas.width, previewHeight / canvas.height)
  const finalScale = baseScale * settings.scale

  const scaledWidth = canvas.width * finalScale
  const scaledHeight = canvas.height * finalScale

  const centerX = (previewWidth - scaledWidth) / 2
  const centerY = (previewHeight - scaledHeight) / 2

  // Apply pan (scaled down for preview)
  const panScale = previewWidth / DISPLAY_WIDTH
  const dx = centerX + settings.panX * finalScale * panScale
  const dy = centerY + settings.panY * finalScale * panScale

  previewCtx.drawImage(canvas, dx, dy, scaledWidth, scaledHeight)

  return previewCanvas.toDataURL('image/jpeg', 0.8)
}

/**
 * Generate a full-size preview with dithering applied
 */
export async function generateFullPreview(
  source: string | File | Blob,
  settings: ImageTransformSettings
): Promise<string> {
  const { previewDataUrl } = await processImageWithSettings(source, settings)
  return previewDataUrl
}

/**
 * Pannable preview result containing the full dithered image and positioning info
 */
export interface PannablePreview {
  dataUrl: string
  width: number   // Full scaled image width
  height: number  // Full scaled image height
}

/**
 * Generate a pannable preview - renders the FULL scaled/dithered image (not cropped to viewport).
 * This allows smooth CSS-based panning without regenerating the preview during drag.
 */
export async function generatePannablePreview(
  source: string | File | Blob,
  settings: Omit<ImageTransformSettings, 'panX' | 'panY'>
): Promise<PannablePreview> {
  // Load the original image
  const img = await loadImage(source)

  // Create initial canvas from image
  let canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0)

  // Apply crop first (if enabled)
  canvas = cropCanvas(canvas, settings.crop)

  // Apply rotation
  canvas = rotateCanvas(canvas, settings.rotation)

  // Apply flips
  canvas = flipCanvas(canvas, settings.flipHorizontal, settings.flipVertical)

  // Calculate base scale to fit image in display
  const baseScale = Math.min(DISPLAY_WIDTH / canvas.width, DISPLAY_HEIGHT / canvas.height)

  // Apply user scale on top of base scale
  const finalScale = baseScale * settings.scale

  // Calculate scaled dimensions
  const scaledWidth = Math.round(canvas.width * finalScale)
  const scaledHeight = Math.round(canvas.height * finalScale)

  // Create scaled canvas (full size, not cropped to viewport)
  const scaledCanvas = document.createElement('canvas')
  scaledCanvas.width = scaledWidth
  scaledCanvas.height = scaledHeight
  const scaledCtx = scaledCanvas.getContext('2d')!

  // Fill with white background
  scaledCtx.fillStyle = 'white'
  scaledCtx.fillRect(0, 0, scaledWidth, scaledHeight)

  // Draw scaled image
  scaledCtx.drawImage(canvas, 0, 0, scaledWidth, scaledHeight)

  // Get image data for dithering
  const imageData = scaledCtx.getImageData(0, 0, scaledWidth, scaledHeight)

  // Apply dithering to the full image
  const dithered = applyDithering(imageData, settings.ditherAlgorithm)

  // Create output canvas with dithered data
  const outputCanvas = document.createElement('canvas')
  outputCanvas.width = scaledWidth
  outputCanvas.height = scaledHeight
  outputCanvas.getContext('2d')!.putImageData(dithered, 0, 0)

  return {
    dataUrl: outputCanvas.toDataURL('image/png'),
    width: scaledWidth,
    height: scaledHeight
  }
}
